import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { anchorRef, bindTrigger, usePopupState, bindPopover } from 'material-ui-popup-state/hooks';
import { Box, Popover, Stack } from '@mui/material';
import { Form, FormRenderProps } from 'react-final-form';

import {
  Button,
  TableContainer,
  TableHeader,
  Typography,
  Select,
  FilterButton,
  isFiltersSelected,
  SearchForm,
} from '@occc-app/design-system';
import { deletePaginationParams, usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useGetGeneralSurveys } from '@occc-app/dataAccessLayer/survey/queries';
import { useQueryParam } from '@occc-app/hooks/useQueryParam';
import { GeneralSurveyFragment, SurveyFilter } from '@occc/graphql';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { useChainId } from '@occc-app/Chain/hooks';

import { GeneralSurveyFilterFormValues, GeneralSurveyStatusType } from './types';
import { SurveysTable } from './SurveysTable';

import { messages } from '../messages';
import { STATUS_PARAM, GENERAL_SURVEY_STATUS_ALL, statusFilterOptions } from '../constants';
import { routeCreator } from '../routeCreator';
import { ArchiveSurveyConfirmDialog } from '../components/ArchiveSurveyConfirmDialog';
import { useArchiveGeneralSurvey } from '../hooks/useArchiveGeneralSurvey';
import { useSearchParams } from '../../../hooks/useSearchParams';

export const SurveyTableContainer = () => {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const chainId = useChainId();

  const isUserPermittedToEditSurvey = useIsUserPermittedTo(Feature.SURVEY_EDIT);
  const isUserPermittedToArchiveSurvey = useIsUserPermittedTo(Feature.SURVEY_ARCHIVE);

  const filterPopupState = usePopupState({ variant: 'popover', popupId: 'filters' });

  const [surveyToArchive, setSurveyToArchive] = useState<null | GeneralSurveyFragment>(null);

  const [status, setStatus] = useQueryParam<GeneralSurveyStatusType>(
    (searchParams) => {
      const status = searchParams.get(STATUS_PARAM) ?? GENERAL_SURVEY_STATUS_ALL;
      return status as GeneralSurveyStatusType;
    },
    (value, searchParams) => {
      if (value && value !== GENERAL_SURVEY_STATUS_ALL) {
        searchParams.set(STATUS_PARAM, value);
      } else {
        searchParams.delete(STATUS_PARAM);
      }

      deletePaginationParams(searchParams);
    },
  );

  const [paginationInfo, onPageChange] = usePaginationParams();

  const filter: SurveyFilter = useMemo(
    () => ({
      search,
      statusEquals: status !== GENERAL_SURVEY_STATUS_ALL ? status : undefined,
      chainIdEquals: chainId,
    }),
    [search, status, chainId],
  );

  const { loading, error, data, paginationProps } = useGetGeneralSurveys({
    paginationInfo,
    onPageChange,
    baseOptions: { variables: { filter } },
  });

  const handleCloseArchiveConfirmationDialog = useCallback(() => {
    setSurveyToArchive(null);
  }, []);

  const [archiveSurvey, archiveSurveyLoading] = useArchiveGeneralSurvey({
    onCompleted: handleCloseArchiveConfirmationDialog,
    refetchQueries: ['generalSurveys'],
  });

  const onArchiveGeneralSurvey = useCallback(async () => {
    if (!surveyToArchive) {
      return;
    }

    archiveSurvey(surveyToArchive.uuid);
  }, [archiveSurvey, surveyToArchive]);

  const onViewDetails = useCallback(
    (item: GeneralSurveyFragment) => {
      navigate(routeCreator.details(item.uuid));
    },
    [navigate],
  );

  const onEdit = useCallback(
    (item: GeneralSurveyFragment) => {
      navigate(routeCreator.update(item.uuid));
    },
    [navigate],
  );

  const filterPopoverProps = useMemo(() => {
    return {
      ...bindPopover({
        ...filterPopupState,
      }),
    };
  }, [filterPopupState]);

  const renderActions = () => {
    return (
      <Stack direction='row' alignItems='center' gap={1}>
        <SearchForm onSubmit={({ search }) => setSearch(search)} initialValues={{ search }} />
        <FilterButton
          {...bindTrigger(filterPopupState)}
          ref={anchorRef(filterPopupState)}
          showBadge={isFiltersSelected([status], [GENERAL_SURVEY_STATUS_ALL])}
        />
      </Stack>
    );
  };

  const onFilterFormSubmit = (values: GeneralSurveyFilterFormValues) => {
    setStatus(values.status);
    filterPopoverProps.onClose();
  };

  const onFilterFormReset = useCallback(() => {
    setStatus(undefined);
    filterPopoverProps.onClose();
  }, [filterPopoverProps, setStatus]);

  const renderFilterForm: FormRenderProps<GeneralSurveyFilterFormValues>['render'] = useCallback(
    ({ handleSubmit, form }: FormRenderProps<GeneralSurveyFilterFormValues>) => {
      const reset = () => {
        form.reset();
        onFilterFormReset();
      };

      return (
        <form onSubmit={handleSubmit}>
          <Box px={4} pt={4}>
            <Typography paragraph>Status</Typography>
            <Select name='status' label='Status' data={statusFilterOptions} />
          </Box>
          <Box display='flex' justifyContent='flex-end' px={3} py={2}>
            <Button size='small' color='error' onClick={reset}>
              reset
            </Button>
            <Button type='submit' size='small' color='primary'>
              apply
            </Button>
          </Box>
        </form>
      );
    },
    [onFilterFormReset],
  );

  return (
    <>
      <TableContainer>
        <TableHeader title={messages.title} actions={renderActions()} />
        <SurveysTable
          surveys={data}
          loading={loading}
          error={error}
          paginationProps={paginationProps}
          isUserPermittedToEditSurvey={isUserPermittedToEditSurvey}
          isUserPermittedToArchiveSurvey={isUserPermittedToArchiveSurvey}
          onEdit={onEdit}
          onViewDetails={onViewDetails}
          onArchive={setSurveyToArchive}
        />
        <Popover
          {...filterPopoverProps}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          keepMounted
          PaperProps={{ sx: { width: 300 } }}
        >
          <Form<GeneralSurveyFilterFormValues>
            render={renderFilterForm}
            onSubmit={onFilterFormSubmit}
            initialValues={{ status }}
          />
        </Popover>
      </TableContainer>

      {surveyToArchive && (
        <ArchiveSurveyConfirmDialog
          onClose={handleCloseArchiveConfirmationDialog}
          onConfirm={onArchiveGeneralSurvey}
          loading={archiveSurveyLoading}
          surveyName={surveyToArchive.name}
        />
      )}
    </>
  );
};
